import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';
import LoginPage from '@/components/auth/LoginPage';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import AppLayout from '@/components/layout/AppLayout';
import AdminUsersPage from '@/pages/AdminUsersPage';
import ErrorBoundary from '@/components/ui/ErrorBoundary';
import { ToastContainer } from '@/components/ui';
import { LearningTracker } from '@/components/learning';
import { startNewSession } from '@/api/ares';

export default function App() {
  const restoreAuth = useAuthStore((s) => s.restoreAuth);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const theme = useUIStore((s) => s.theme);

  // Restore auth on mount
  useEffect(() => {
    restoreAuth();
  }, [restoreAuth]);

  // Start new session when user becomes authenticated
  // This cleans conversation context from previous sessions
  useEffect(() => {
    if (isAuthenticated) {
      // Small delay to ensure auth is fully restored
      const timer = setTimeout(() => {
        startNewSession(true)
          .then((response) => {
            console.log('✅ New session started:', response.message);
            if (response.cleared_keys_count > 0) {
              console.log(`🧹 Cleared ${response.cleared_keys_count} keys from previous session`);
            }
          })
          .catch((error) => {
            // Non-critical error - app can still function
            console.warn('⚠️ Could not start new session:', error);
          });
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isAuthenticated]);

  // Apply theme on mount
  useEffect(() => {
    document.documentElement.setAttribute(
      'data-theme',
      theme === 'dark' ? '' : theme
    );
  }, [theme]);

  return (
    <ErrorBoundary>
      <BrowserRouter future={{ v7_relativeSplatPath: true }}>
        <LearningTracker />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute>
                <AdminUsersPage />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <ToastContainer />
      </BrowserRouter>
    </ErrorBoundary>
  );
}
